import type { ICDARepository } from "../repositories/CDARepository";
import type {
  CDARequest,
  CDATemplate,
  CDAListResponse,
  CDAStatistics,
  CreateCDARequestPayload,
  SignCDARequestPayload,
  RejectCDARequestPayload,
  ClarificationCDARequestPayload,
  DeleteCDARequestPayload,
  CreateCDATemplatePayload,
  UpdateCDATemplatePayload,
  CDARequestFilters,
  CDATemplateFilters,
  CDAAuditEntry,
  UploadManualCDAPayload,
  ManualCDAUploadResult,
} from "../types/cda.type";

// ─── Interface ────────────────────────────────────────────────────────────────

export interface ICDAUseCase {
  // CDA Request operations
  listRequests(
    filters?: CDARequestFilters,
  ): Promise<CDAListResponse<CDARequest>>;
  getRequestById(id: string): Promise<CDARequest>;
  createRequests(payload: CreateCDARequestPayload): Promise<CDARequest[]>;
  resendRequest(id: string): Promise<CDARequest>;
  signRequest(id: string, payload: SignCDARequestPayload): Promise<CDARequest>;
  rejectRequest(
    id: string,
    payload: RejectCDARequestPayload,
  ): Promise<CDARequest>;
  requestClarification(
    id: string,
    payload: ClarificationCDARequestPayload,
  ): Promise<CDARequest>;
  cancelRequest(id: string): Promise<CDARequest>;
  deleteManualRequest(
    id: string,
    payload: DeleteCDARequestPayload,
  ): Promise<CDARequest>;
  fileToEtmf(id: string): Promise<CDARequest>;
  markViewed(id: string): Promise<CDARequest>;
  getAuditTrail(id: string): Promise<CDAAuditEntry[]>;

  // CDA Template operations
  listTemplates(
    filters?: CDATemplateFilters,
  ): Promise<CDAListResponse<CDATemplate>>;
  createTemplate(payload: CreateCDATemplatePayload): Promise<CDATemplate>;
  updateTemplate(
    id: string,
    payload: UpdateCDATemplatePayload,
  ): Promise<CDATemplate>;
  activateTemplate(id: string): Promise<CDATemplate>;
  archiveTemplate(id: string): Promise<CDATemplate>;
  duplicateTemplate(id: string): Promise<CDATemplate>;

  // Statistics
  getStatistics(studyId?: string): Promise<CDAStatistics>;

  // Manual upload (no eSignature flow — direct eTMF filing)
  uploadManualCDA(
    payload: UploadManualCDAPayload,
  ): Promise<ManualCDAUploadResult>;

  /** PI email signing page — unauthenticated API paths (`/cda/public/...`). */
  getRequestByIdForEmailLink(id: string): Promise<CDARequest>;
  markViewedForEmailLink(id: string): Promise<CDARequest>;
  signRequestForEmailLink(
    id: string,
    payload: SignCDARequestPayload,
  ): Promise<CDARequest>;
  rejectRequestForEmailLink(
    id: string,
    payload: RejectCDARequestPayload,
  ): Promise<CDARequest>;
  requestClarificationForEmailLink(
    id: string,
    payload: ClarificationCDARequestPayload,
  ): Promise<CDARequest>;
  /** Fetch the CDA document as a Blob so the iframe can load it via an object URL (avoids CORS/auth header limitations). */
  getDocumentBlobForEmailLink(id: string): Promise<Blob>;
}

// ─── Implementation ───────────────────────────────────────────────────────────

export class CDAUseCase implements ICDAUseCase {
  constructor(private readonly cdaRepository: ICDARepository) {}

  // ── CDA Requests ──────────────────────────────────────────────────────────

  async listRequests(
    filters?: CDARequestFilters,
  ): Promise<CDAListResponse<CDARequest>> {
    return this.cdaRepository.listRequests(filters);
  }

  async getRequestById(id: string): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    return this.cdaRepository.getRequestById(id);
  }

  async createRequests(
    payload: CreateCDARequestPayload,
  ): Promise<CDARequest[]> {
    if (!payload.studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!payload.siteIds || payload.siteIds.length === 0) {
      throw new Error("At least one site must be selected");
    }
    if (!payload.templateId?.trim()) {
      throw new Error("Template is required");
    }
    if (!payload.dueDate) {
      throw new Error("Due date is required");
    }

    const due = new Date(payload.dueDate);
    if (due <= new Date()) {
      throw new Error("Due date must be in the future");
    }
    return this.cdaRepository.createRequests(payload);
  }

  async resendRequest(id: string): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    // Backend validates status transition (Signed/Expired → 400)
    return this.cdaRepository.resendRequest(id);
  }

  async signRequest(
    id: string,
    payload: SignCDARequestPayload,
  ): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    if (!payload.signerName?.trim()) {
      throw new Error("Signer name is required");
    }
    if (!payload.signerRole?.trim()) {
      throw new Error("Signer role is required");
    }

    // Backend validates status transitions — returns 400 on violation
    return this.cdaRepository.signRequest(id, payload);
  }

  async rejectRequest(
    id: string,
    payload: RejectCDARequestPayload,
  ): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    if (!payload.reason?.trim()) {
      throw new Error("Rejection reason is required");
    }

    return this.cdaRepository.rejectRequest(id, payload);
  }

  async requestClarification(
    id: string,
    payload: ClarificationCDARequestPayload,
  ): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    if (!payload.message?.trim()) {
      throw new Error("Clarification message is required");
    }
    return this.cdaRepository.requestClarification(id, payload);
  }

  async cancelRequest(id: string): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    // Backend validates that Signed CDAs cannot be cancelled
    return this.cdaRepository.cancelRequest(id);
  }

  async deleteManualRequest(
    id: string,
    payload: DeleteCDARequestPayload,
  ): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    if (!payload.reason?.trim()) {
      throw new Error("Delete reason is required");
    }
    return this.cdaRepository.deleteManualRequest(id, {
      reason: payload.reason.trim(),
      comments: payload.comments?.trim() ?? undefined,
    });
  }

  async fileToEtmf(id: string): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    return this.cdaRepository.fileToEtmf(id);
  }

  async markViewed(id: string): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    return this.cdaRepository.markViewed(id);
  }

  async getAuditTrail(id: string): Promise<CDAAuditEntry[]> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    return this.cdaRepository.getAuditTrail(id);
  }

  // ── CDA Templates ─────────────────────────────────────────────────────────

  async listTemplates(
    filters?: CDATemplateFilters,
  ): Promise<CDAListResponse<CDATemplate>> {
    return this.cdaRepository.listTemplates(filters);
  }

  async createTemplate(
    payload: CreateCDATemplatePayload,
  ): Promise<CDATemplate> {
    if (!payload.name?.trim()) {
      throw new Error("Template name is required");
    }
    if (!payload.content?.trim()) {
      throw new Error("Template content is required");
    }
    return this.cdaRepository.createTemplate(payload);
  }

  async updateTemplate(
    id: string,
    payload: UpdateCDATemplatePayload,
  ): Promise<CDATemplate> {
    if (!id?.trim()) {
      throw new Error("Template ID is required");
    }
    return this.cdaRepository.updateTemplate(id, payload);
  }

  async activateTemplate(id: string): Promise<CDATemplate> {
    if (!id?.trim()) {
      throw new Error("Template ID is required");
    }
    return this.cdaRepository.activateTemplate(id);
  }

  async archiveTemplate(id: string): Promise<CDATemplate> {
    if (!id?.trim()) {
      throw new Error("Template ID is required");
    }
    return this.cdaRepository.archiveTemplate(id);
  }

  async duplicateTemplate(id: string): Promise<CDATemplate> {
    if (!id?.trim()) {
      throw new Error("Template ID is required");
    }
    return this.cdaRepository.duplicateTemplate(id);
  }

  // ── Statistics ────────────────────────────────────────────────────────────

  async getStatistics(studyId?: string): Promise<CDAStatistics> {
    return this.cdaRepository.getStatistics(studyId);
  }
  // ── Manual Upload ────────────────────────────────────────────────

  async uploadManualCDA(
    payload: UploadManualCDAPayload,
  ): Promise<ManualCDAUploadResult> {
    if (!payload.studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!payload.siteId?.trim()) {
      throw new Error("Site is required");
    }
    if (!payload.cdaTitle?.trim()) {
      throw new Error("CDA Title is required");
    }
    if (!payload.signedDate) {
      throw new Error("Signed Date is required");
    }
    if (!payload.signedPdf) {
      throw new Error("Signed PDF is required");
    }
    return this.cdaRepository.uploadManualCDA(payload);
  }
  async getRequestByIdForEmailLink(id: string): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    return this.cdaRepository.getPublicRequestById(id);
  }

  async markViewedForEmailLink(id: string): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    return this.cdaRepository.markViewedViaPublicLink(id);
  }

  async signRequestForEmailLink(
    id: string,
    payload: SignCDARequestPayload,
  ): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    if (!payload.signerName?.trim()) {
      throw new Error("Signer name is required");
    }
    if (!payload.signerRole?.trim()) {
      throw new Error("Signer role is required");
    }
    if (!payload.signedPdfBase64?.trim()) {
      throw new Error("Signed PDF payload is required");
    }
    return this.cdaRepository.signRequestViaPublicLink(id, payload);
  }

  async rejectRequestForEmailLink(
    id: string,
    payload: RejectCDARequestPayload,
  ): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    if (!payload.reason?.trim()) {
      throw new Error("Rejection reason is required");
    }
    return this.cdaRepository.rejectRequestViaPublicLink(id, payload);
  }

  async requestClarificationForEmailLink(
    id: string,
    payload: ClarificationCDARequestPayload,
  ): Promise<CDARequest> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    if (!payload.message?.trim()) {
      throw new Error("Clarification message is required");
    }
    return this.cdaRepository.requestClarificationViaPublicLink(id, payload);
  }

  async getDocumentBlobForEmailLink(id: string): Promise<Blob> {
    if (!id?.trim()) {
      throw new Error("CDA Request ID is required");
    }
    return this.cdaRepository.getDocumentBlob(id);
  }
}
